'use client';

import { useTranslations } from 'next-intl';
import { SAVINGS_GOAL, savingsSummary } from '@/src/shared/lib/chart';
import { usePrefersReducedMotion } from '@/src/shared/lib/usePrefersReducedMotion';
import { SavingsRateBars } from './savings-rate-bars';
import type { SavingsRateBarsProps } from './savings-rate-bars.types';
import { SavingsRateSummary } from './savings-rate-summary';

export interface SavingsRateChartProps extends Omit<SavingsRateBarsProps, 'labels'> {
  labels: Omit<SavingsRateBarsProps['labels'], 'goal'>;
  /** The period's rate, weighted by income; null when no month had any. */
  average: number | null;
  /** A month's name as it reads mid-sentence: "octubre". */
  monthName: (month: string) => string;
}

/**
 * The landing's monthly savings rate over real months: the latest month and the
 * period against the goal beside it, then a column per month against the goal line.
 */
export function SavingsRateChart(props: SavingsRateChartProps) {
  const { average, monthName, labels, ...bars } = props;
  const t = useTranslations('common.savingsChart');
  const reduced = usePrefersReducedMotion();
  const summary = savingsSummary(props.bars);

  return (
    <div className="grid gap-6 sm:grid-cols-[210px_1fr] sm:items-end">
      <SavingsRateSummary
        summary={summary}
        average={average}
        total={props.bars.length}
        withIncome={props.bars.filter((b) => b.pct !== null).length}
        locale={props.locale}
        month={monthName(summary.latest.month)}
        noIncome={labels.noIncome}
        formatMoney={props.formatMoney}
        reduced={reduced}
      />
      <SavingsRateBars {...bars} labels={{ ...labels, goal: t('goal', { pct: SAVINGS_GOAL }) }} />
    </div>
  );
}
